import { useEffect, useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, Textarea } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { reopenFinancing } from '../api';
import type { FinancingRecord } from '../types';
import styles from './dialogs.module.css';

export interface ReopenDialogProps {
  open: boolean;
  financing: FinancingRecord;
  onClose: () => void;
  onDone: (financing: FinancingRecord) => void;
}

/** „Wieder öffnen" (admin, no frame; pattern of Deal-Akte 02): mandatory reason → `POST /financings/:id/reopen`. */
export function ReopenDialog({ open, financing, onClose, onDone }: ReopenDialogProps) {
  const formId = useId();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const stage = financing.stageBar.find((segment) => segment.state === 'active')?.label ?? null;

  useEffect(() => {
    if (!open) return;
    setReason('');
    setError(null);
    setBanner(null);
  }, [open, financing.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    if (reason.trim() === '') {
      setError('Bitte geben Sie einen Grund an.');
      return;
    }
    setPending(true);
    setBanner(null);
    try {
      onDone(await reopenFinancing(financing.id, reason.trim()));
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields['reason']) setError(cause.fields['reason']);
      else setBanner(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      eyebrow="Wieder öffnen"
      title={`Finanzierung ${financing.numberLabel} wieder öffnen`}
      subtitle={financing.title}
      footerNote={stage ? `Die Finanzierung startet wieder in „${stage}“.` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending}>
            Wieder öffnen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <Textarea
          label="Grund"
          required
          rows={3}
          value={reason}
          error={error ?? undefined}
          onChange={(event) => setReason(event.target.value)}
        />
      </form>
    </Dialog>
  );
}
